import { useEffect, useRef, useState } from 'react';
import { create } from 'zustand';
import { Button, Input, Modal } from './ui';

interface DialogRequest {
  kind: 'prompt' | 'confirm';
  title: string;
  body?: string;
  placeholder?: string;
  initial?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  resolve: (v: string | boolean | null) => void;
}

const useDialogs = create<{ current: DialogRequest | null }>(() => ({ current: null }));

/** Electron has no window.prompt, so text input goes through this themed dialog. */
export function askText(title: string, opts: { placeholder?: string; initial?: string; confirmLabel?: string; body?: string } = {}): Promise<string | null> {
  return new Promise((resolve) => useDialogs.setState({ current: { kind: 'prompt', title, ...opts, resolve: (v) => resolve(typeof v === 'string' ? v : null) } }));
}

export function confirmDialog(title: string, body?: string, opts: { confirmLabel?: string; cancelLabel?: string; danger?: boolean } = {}): Promise<boolean> {
  return new Promise((resolve) => useDialogs.setState({ current: { kind: 'confirm', title, body, ...opts, resolve: (v) => resolve(v === true) } }));
}

export function DialogHost() {
  const req = useDialogs((s) => s.current);
  const [value, setValue] = useState('');
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    setValue(req?.initial ?? '');
    if (req?.kind === 'prompt') setTimeout(() => input.current?.focus(), 50);
  }, [req]);
  const close = (v: string | boolean | null) => {
    req?.resolve(v);
    useDialogs.setState({ current: null });
  };
  return (
    <Modal open={!!req} onClose={() => close(null)} width={420}>
      {req && (
        <form
          className="space-y-4 p-5"
          onSubmit={(e) => {
            e.preventDefault();
            close(req.kind === 'prompt' ? value.trim() || null : true);
          }}
        >
          <div className="text-[15px] font-semibold">{req.title}</div>
          {req.body && <p className="text-[13px] text-muted">{req.body}</p>}
          {req.kind === 'prompt' && <Input ref={input} value={value} onChange={(e) => setValue(e.target.value)} placeholder={req.placeholder} />}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => close(null)}>
              {req.cancelLabel ?? 'Cancel'}
            </Button>
            <Button type="submit" variant={req.danger ? 'danger' : 'primary'}>
              {req.confirmLabel ?? 'OK'}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
